/**
 * ч.746 (рішення власника): спрощений ввід назви організації —
 * «Оргформа» (пошук по ОПФГ ДК 002:2004) → «Назва (без оргформи)» → «Скорочено»
 * (ВЕСЬ текст короткої назви як хоче юзер; порожнє → проєкт, який поле показує
 * підказкою, — ч.1192: підказка і значення за замовчуванням — одна функція
 * defaultShortName). Повна і коротка назви компілюються (бекенд
 * авторитетний, тут live-прев'ю через compileOrgNames). Оргформа ФОП →
 * entity_type='FOP' (РНОКПП замість ЄДРПОУ), інші → юрособа.
 * Використовується у картці організації («Основне») та кроці 1 майстра.
 */
import type { OrganizationCreate } from '@/api/catalogs';
import SearchSelect from '@/components/SearchSelect';
import {
  OPFG_CODES, compileOrgNames, defaultShortName, findOpfgByKey, isFopOpfg, opfgKey, opfgKeyFor,
} from '@/shared/ukr_classifiers';
import { vatFixedFor } from '@/shared/components/VatPayerSelect';

/** Підказка у полі «Скорочено» = рівно те, що стане короткою назвою, якщо поле
 *  лишити порожнім; поки власної назви немає — той самий проєкт на прикладі. */
export function shortNamePlaceholder(f: OrganizationCreate): string {
  const example = isFopOpfg(f.opfg_code, f.opfg_name) ? 'Іванов І.І.' : 'Ромашка';
  return defaultShortName({
    opfg_code: f.opfg_code, opfg_name: f.opfg_name,
    own_name: (f.own_name ?? '').trim() || example,
  });
}

/** Перекомпілювати name/full_name у формі (якщо own_name заданий). */
export function withCompiledNames(f: OrganizationCreate): OrganizationCreate {
  const c = compileOrgNames({
    opfg_code: f.opfg_code, opfg_name: f.opfg_name,
    own_name: f.own_name ?? null, opf_short: f.opf_short ?? null,
  });
  return c ? { ...f, name: c.name, full_name: c.full_name } : f;
}

/** Обрати оргформу (за ключем списку, ч.1195): код/назва + entity_type + дефолт
 *  системи оподаткування + скорочення. */
export function applyOpfgToForm(f: OrganizationCreate, key: string | null): OrganizationCreate {
  const opt = findOpfgByKey(key);
  const code = opt?.code ?? null;
  const opfgName = opt?.name ?? null;
  const et: OrganizationCreate['entity_type'] = isFopOpfg(code, opfgName) ? 'FOP' : 'LEGAL_ENTITY';
  const etChanged = et !== f.entity_type;
  // «Скорочено» юзер пише САМ (уточнення власника 2026-09-09) — при виборі
  // оргформи нічого не підставляємо; підказка лише у placeholder.
  const taxSystem: OrganizationCreate['tax_system'] = etChanged
    ? (et === 'LEGAL_ENTITY' ? 'GENERAL' : 'FOP_2')
    : f.tax_system;
  return withCompiledNames({
    ...f,
    opfg_code: code, opfg_name: opfgName, entity_type: et,
    tax_system: taxSystem,
    // ч.1381: юрособа — знову «оберіть» (раніше ставилось «ні» мовчки), ФОП 2 гр. — «ні».
    is_vat_payer: etChanged ? vatFixedFor(taxSystem) : f.is_vat_payer,
    vat_number: etChanged ? null : f.vat_number,
  });
}

const OPFG_ITEMS = OPFG_CODES.map((o) => ({ id: opfgKey(o), label: o.name, hint: o.code }));

export default function OrgNameFields(props: {
  form: OrganizationCreate;
  setForm: (f: OrganizationCreate) => void;
}) {
  const { form, setForm } = props;
  const isFop = form.entity_type === 'FOP';
  return (
    <>
      <div className="field" style={{ width: 300 }}>
        <label>Оргформа *</label>
        <SearchSelect
          items={OPFG_ITEMS}
          value={opfgKeyFor(form.opfg_code, form.opfg_name)}
          placeholder="ТОВ, ПП, ФОП…"
          onChange={(id) => setForm(applyOpfgToForm(form, id || null))}
        />
      </div>
      <div className="field" style={{ width: isFop ? 380 : 320 }}>
        <label>{isFop ? 'ПІБ *' : 'Назва (без оргформи) *'}</label>
        <input
          value={form.own_name ?? ''}
          disabled={!form.opfg_code}
          placeholder={!form.opfg_code ? 'спершу оберіть оргформу' : (isFop ? 'Шевченко Тарас Григорович' : 'Ромашка')}
          onChange={(e) => setForm(withCompiledNames({ ...form, own_name: e.target.value || null }))}
        />
      </div>
      <div className="field" style={{ width: 260 }}>
        <label>Скорочено <small style={{ color: 'var(--muted)' }}>(коротка назва як є)</small></label>
        <input
          value={form.opf_short ?? ''}
          disabled={!form.opfg_code}
          placeholder={shortNamePlaceholder(form)}
          title="Якщо лишити порожнім — коротка назва буде такою, як у підказці"
          maxLength={255}
          onChange={(e) => setForm(withCompiledNames({ ...form, opf_short: e.target.value || null }))}
        />
      </div>
    </>
  );
}

/** Рядок-прев'ю скомпільованих назв (те, що піде у документи та звіти). */
export function OrgNamePreview({ form }: { form: OrganizationCreate }) {
  if (!(form.own_name ?? '').trim()) return null;
  return (
    <div style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: '-0.25rem 0 0.5rem' }}>
      {form.name}
      <span style={{ margin: '0 0.5rem' }}>·</span>
      {form.full_name}
    </div>
  );
}
